import React, { useEffect, useState, useRef, useImperativeHandle } from "react";
import { NumericFormat } from "react-number-format";
import { TextField } from "@mui/material";
import { StyleSettings } from "../../styles/StyleSettings";
import {
  fnGetConfigFormat,
  fnFormatNumberByPattern,
} from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용

interface SnNumberBoxProps {
  defaultValue?: number;
  onValueChanged?: (value: number) => void;
  format?: string;
  customFormat?: string;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isEditable?: boolean;
  inputRef?: any;
  isSearchPanel?: boolean;
  onFocus?: (target: string) => void;
  onEnter?: (value: number) => Promise<void> | void;
  isAutoClear?: boolean;
  inputHeight?: number;
  fontSize?: number;
  backgroundColor?: string;
  borderWidth?: string;
  readOnly?: boolean; // disabled와 달리 글자색 유지, 입력만 차단
}

const SnNumberBox = React.memo(
  ({
    defaultValue,
    onValueChanged,
    format = "",
    customFormat = "",
    isReadOnly = false,
    isRequired,
    isEditable = true,
    inputRef,
    isSearchPanel = false,
    onFocus,
    onEnter,
    isAutoClear = false,
    inputHeight = StyleSettings.control_height,
    fontSize = StyleSettings.font,
    backgroundColor: backgroundColorProp,
    borderWidth: borderWidthProp,
    readOnly = false,
  }: SnNumberBoxProps) => {
    const internalInputRef = useRef<HTMLInputElement | null>(null);

    const [value, setValue] = useState(defaultValue || 0);
    const [appliedFormat, setAppliedFormat] = useState(format);
    const [isFocused, setIsFocused] = useState(false);

    useImperativeHandle(inputRef, () => ({
      get getValue() {
        return value;
      },
      setValue: (v: number) => setValue(v),
      focus: () => {
        const el = internalInputRef.current;

        if (el) {
          el.focus();
          el.select?.();
        }
      },
    }));

    const [borderWidth, setBorderWidth] = useState("1px");
    const [backgroundColor, setBackgroundColor] = useState("#F3F3F3");
    const fetchDefaultValue = async () => {
      try {
        const newValue = defaultValue ? defaultValue : 0;
        if (value !== newValue) {
          setValue(newValue);
        }
      } catch (error: any) {}
    };
    const fetchIsRequired = async () => {
      try {
        if (borderWidthProp) setBorderWidth(borderWidthProp);
        else if (isRequired) setBorderWidth("2px");
        else setBorderWidth("1px");
      } catch (error: any) {}
    };

    const fetchIsEditable = async () => {
      try {
        if (backgroundColorProp) {
          setBackgroundColor(backgroundColorProp);
          return;
        }
        if (isEditable) {
          if (isReadOnly) setBackgroundColor("#F3F3F3");
          else {
            setBackgroundColor("white");
          }
        } else {
          setBackgroundColor("#F3F3F3");
        }
      } catch (error: any) {}
    };

    useEffect(() => {
      fetchDefaultValue();
    }, [defaultValue]);

    useEffect(() => {
      fetchIsRequired();
    }, [isRequired, borderWidthProp]);

    useEffect(() => {
      fetchIsEditable();
    }, [isEditable, isReadOnly, backgroundColorProp]);

    useEffect(() => {
      const loadCustomFormat = async () => {
        if (customFormat && customFormat !== "") {
          try {
            // fnGetConfigFormat이 Promise를 반환한다고 가정
            const config = await fnGetConfigFormat(customFormat);
            // API 결과에서 포맷 문자열 추출 (예: config.format 또는 config 자체)
            if (config) {
              setAppliedFormat(config);
            }
          } catch (error) {
            setAppliedFormat(format); // 실패 시 기본 format 사용
          }
        } else {
          setAppliedFormat(format); // customFormat 없으면 기본 prop 사용
        }
      };
      loadCustomFormat();
    }, [customFormat, format]); // 의존성 배열에 추가

    const handleValueChange = (values: any) => {
      setValue(values.floatValue || 0); // floatValue로 숫자 관리
    };

    const handleBlur = () => {
      setIsFocused(false);
      if (onValueChanged) {
        onValueChanged(value); // 포커스가 빠졌을 때만 호출
      }
    };

    const handleKeyDown = async (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        await (e.target as HTMLInputElement).blur();
        if (isSearchPanel) {
          e.preventDefault();
          onFocus && onFocus("current");
        } else {
          e.preventDefault();
          onFocus && onFocus("next");
        }

        if (!value || value === 0) return; // 빈 값일 때는 실행 방지

        await onEnter?.(value);
        if (isAutoClear) {
          setValue(0);
        }
      } else if (e.key === "Tab") {
        await (e.target as HTMLInputElement).blur();
        e.preventDefault();
        onFocus && onFocus("next");
      }
    };

    const isThousandSeparator = appliedFormat && appliedFormat.includes(",");
    const decimalScale = ((appliedFormat && appliedFormat.split(".")[1]) || "")
      .length;

    const displayValue =
      appliedFormat && !isFocused
        ? fnFormatNumberByPattern(
            value,
            appliedFormat.replace(/,/g, "")
          ).replace(/\.$/, "")
        : value;

    return (
      <NumericFormat
        value={displayValue}
        customInput={TextField}
        inputRef={(el: HTMLInputElement | null) => {
          internalInputRef.current = el;
        }}
        thousandSeparator={isThousandSeparator}
        decimalScale={decimalScale}
        fixedDecimalScale={!appliedFormat}
        onValueChange={handleValueChange}
        onFocus={() => setIsFocused(true)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        fullWidth
        sx={{
          "& input": { textAlign: "right" },
          fontSize: 12,
          "& .MuiOutlinedInput-root": {
            "&": {
              backgroundColor: backgroundColor, //
            },
            "& fieldset": {
              borderWidth: borderWidth, // 테두리 두께 설정
            },
            "&:hover fieldset": {
              borderWidth: borderWidth, // 호버 시 두께 유지
            },
            "&.Mui-focused fieldset": {
              borderWidth: borderWidth, // 포커스 시 두께 유지
            },
          },
        }}
        slotProps={{
          input: {
            inputRef: internalInputRef,
            readOnly: readOnly,
            style: {
              height: inputHeight,
              fontSize: fontSize,
            },
          },
        }}
        disabled={isReadOnly}
      />
    );
  }
);

export default SnNumberBox;
